import { FC } from "react";
import { Box, Flex, Heading, HStack, Text, VStack } from "@chakra-ui/react";
import { SlideViewData } from "./types";

const fmt = (sec: number) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

// Pełny widok prezentera (osobne okno na drugim ekranie): bieżący slajd, NASTĘPNY, notatki, zegar.
export const PresenterLayout: FC<{
  current: SlideViewData;
  next?: SlideViewData;
  index: number;
  total: number;
  elapsedSec: number;
  progress: number;
  onPrev: () => void;
  onNext: () => void;
  canPrev: boolean;
  canNext: boolean;
}> = ({ current, next, index, total, elapsedSec, progress, onPrev, onNext, canPrev, canNext }) => (
  <Flex direction="column" height="100vh" bg="#0F0A1F" color="#fff" p="22px" gap="16px">
    <HStack justify="space-between">
      <Text fontWeight="800" letterSpacing="0.06em" color="#FFD23F">
        TRYB PREZENTERA
      </Text>
      <HStack spacing="18px">
        <Text fontSize="22px" fontWeight="800" fontFamily="mono">
          {fmt(elapsedSec)}
        </Text>
        <Text fontSize="16px" color="rgba(255,255,255,0.7)">
          slajd {index + 1} / {total}
        </Text>
      </HStack>
    </HStack>

    <Flex flex="1" gap="18px" minH={0}>
      <VStack flex="1.3" align="stretch" spacing="12px" minW={0}>
        <Box flex="1" bg="rgba(255,255,255,0.05)" border="1px solid rgba(255,255,255,0.12)" borderRadius="16px" p="22px" overflow="auto">
          <Text color="#FFD23F" fontWeight="800" textTransform="uppercase" letterSpacing="0.12em" fontSize="13px">
            Teraz: {current.kicker || ""}
          </Text>
          <Heading mt="6px" fontSize="30px" fontWeight="900" color="#fff">
            {current.title || current.lead || current.id}
          </Heading>
          {current.lead && current.title && (
            <Text mt="8px" fontSize="18px" color="rgba(255,255,255,0.8)">
              {current.lead}
            </Text>
          )}
          <Box mt="16px" h="1px" bg="rgba(255,255,255,0.12)" />
          <Text mt="14px" fontSize="13px" fontWeight="800" color="#9AE6B4" textTransform="uppercase" letterSpacing="0.1em">
            Notatki prezentera
          </Text>
          <Text mt="6px" fontSize="20px" lineHeight="1.5" color="#fff">
            {current.notes || "—"}
          </Text>
        </Box>
      </VStack>

      <VStack flex="1" align="stretch" spacing="12px" minW={0}>
        <Box flex="1" bg="rgba(255,255,255,0.05)" border="1px solid rgba(255,255,255,0.12)" borderRadius="16px" p="22px">
          <Text color="rgba(255,255,255,0.6)" fontWeight="800" textTransform="uppercase" letterSpacing="0.12em" fontSize="13px">
            Następny slajd
          </Text>
          {next ? (
            <>
              <Heading mt="8px" fontSize="24px" fontWeight="800" color="rgba(255,255,255,0.92)">
                {next.title || next.lead || next.id}
              </Heading>
              {next.kicker && (
                <Text mt="6px" fontSize="15px" color="rgba(255,255,255,0.6)">
                  {next.kicker}
                </Text>
              )}
            </>
          ) : (
            <Text mt="8px" fontSize="20px" color="rgba(255,255,255,0.6)">
              To ostatni slajd.
            </Text>
          )}
          {current.durationSec && (
            <Text mt="18px" fontSize="14px" color="rgba(255,255,255,0.5)">
              Sugerowany czas slajdu: ~{current.durationSec}s
            </Text>
          )}
        </Box>
        <HStack spacing="10px">
          <PresBtn label="‹ Wstecz" onClick={onPrev} disabled={!canPrev} />
          <PresBtn label="Dalej ›" onClick={onNext} disabled={!canNext} primary />
        </HStack>
      </VStack>
    </Flex>

    <Box h="6px" bg="rgba(255,255,255,0.1)" borderRadius="full" overflow="hidden">
      <Box h="100%" bg="#E51F44" width={`${Math.round(progress * 100)}%`} />
    </Box>
  </Flex>
);

const PresBtn: FC<{ label: string; onClick: () => void; disabled?: boolean; primary?: boolean }> = ({
  label,
  onClick,
  disabled,
  primary
}) => (
  <Box
    as="button"
    flex="1"
    onClick={onClick}
    aria-disabled={disabled}
    opacity={disabled ? 0.4 : 1}
    cursor={disabled ? "not-allowed" : "pointer"}
    py="14px"
    borderRadius="12px"
    fontSize="18px"
    fontWeight="800"
    bg={primary ? "#E51F44" : "rgba(255,255,255,0.1)"}
    color="#fff"
  >
    {label}
  </Box>
);

// Nakładka z notatkami na JEDNYM ekranie (tryb „notes", klawisz P).
export const NotesOverlay: FC<{ notes?: string; next?: SlideViewData; elapsedSec: number }> = ({
  notes,
  next,
  elapsedSec
}) => (
  <Box
    position="absolute"
    bottom="0"
    left="0"
    right="0"
    bg="rgba(15,10,31,0.94)"
    color="#fff"
    px="28px"
    py="18px"
    maxH="38%"
    overflowY="auto"
  >
    <HStack justify="space-between" mb="6px">
      <Text fontSize="12px" fontWeight="800" color="#9AE6B4" textTransform="uppercase" letterSpacing="0.12em">
        Notatki prezentera
      </Text>
      <HStack spacing="14px">
        {next && (
          <Text fontSize="13px" color="rgba(255,255,255,0.6)">
            Dalej: {next.title || next.kicker || next.id}
          </Text>
        )}
        <Text fontSize="14px" fontWeight="800" fontFamily="mono">
          {fmt(elapsedSec)}
        </Text>
      </HStack>
    </HStack>
    <Text fontSize="18px" lineHeight="1.5">
      {notes || "—"}
    </Text>
  </Box>
);
